<template>
    <q-modal v-model="show_insert_sale" :content-css="{minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '90vh'}" no-backdrop-dismiss no-esc-dismiss @hide="clearSale()">
        <q-modal-layout>
            <q-toolbar slot="header">
                <q-btn flat round dense @click="show_insert_sale = false" icon="fas fa-times" />
                <q-toolbar-title>
                    {{ $t('insert_user_sale') }}
                </q-toolbar-title>
            </q-toolbar>

            <div class="layout-padding">
                <div class="row gutter-sm">
                    <div class="col-12">
                        <q-select filter v-model="company" :options="companies" :float-label="`${$t('retailer')}*`" :error="$v.company.$error" @input="changeCompany()" />
                    </div>

                    <div class="col-12">
                        <q-select filter v-model="branch" :options="branches" :float-label="`${$t('store')}*`" :error="$v.branch.$error" @input="$v.branch.$touch" />
                    </div>

                    <div class="col-xs-12 col-sm-6">
                        <q-datetime v-model="sale_date" type="date" :float-label="`${$t('sale_date')}*`" format="DD/MM/YYYY" :error="$v.sale_date.$error" @input="$v.sale_date.$touch" :min="mininum_date" :max="maximum_date" />
                    </div>

                    <div class="col-xs-12 col-sm-6">
                        <q-field :error="order_number_error" :error-label="$t('the_note_number_has_already_been_sent_by_the_user_before')">
                            <q-input v-model="order_number" :float-label="`${$t('invoice_number')}*`" :error="$v.order_number.$error" @input="$v.order_number.$touch" @blur="orderNumberUnique()" />
                        </q-field>
                    </div>

                    <div class="col-12">
                        <p class="caption">{{ $t('upload_invoice') }}</p>
                        <uploader content_type="images" @fileUploaded="fileUploaded" :multiple="false" :cropper="false" />
                    </div>

                    <div class="col-12" v-if="sale_images.length > 0">
                        <div class="row gutter-sm">
                            <div class="col-xs-4 col-sm-2" v-for="(sale_image, index) in sale_images" :key="index">
                                <img :src="sale_image" style="max-width: 100%; height: auto;" />
                                <q-btn dense icon="fal fa-times" :label="$t('remove_image')" color="negative" class="full-width" @click="removeUserSaleImage(index)" />
                            </div>
                        </div>
                    </div>

                    <div class="col-12">
                        <q-input v-model="observation" :float-label="$t('observation')" type="textarea" />
                    </div>

                    <div class="col-12 caption">
                        {{ $t('detail_sold_products') }}
                    </div>

                    <div class="col-12" v-for="(p, index) in products" :key="index">
                        <q-card>
                            <q-card-main>
                                <div class="row gutter-sm justify-end">
                                    <div class="col-xs-12 col-sm-6">
                                        <q-select v-model="p.brand" :options="brands" :float-label="`${$t('brand')}*`" :error="$v.products.$each[index].brand.$error" @input="$v.products.$each[index].brand.$touch" />
                                    </div>

                                    <div class="col-xs-12 col-sm-6">
                                        <q-select v-model="p.product_type" :options="product_types" :float-label="`${$t('product_type')}*`" :error="$v.products.$each[index].product_type.$error" @input="$v.products.$each[index].product_type.$touch" />
                                    </div>

                                    <div class="col-xs-12 col-sm-6">
                                        <q-select v-model="p.product" :options="processors" :float-label="`${$t('processor')}*`" :error="$v.products.$each[index].product.$error" @input="$v.products.$each[index].product.$touch" />
                                    </div>

                                    <div class="col-xs-12 col-sm-6">
                                        <q-input v-model="p.product_code" :float-label="`${$t('sku')}*`" :error="$v.products.$each[index].product_code.$error" @input="$v.products.$each[index].product_code.$touch" />
                                    </div>

                                    <div class="col-xs-12 col-sm-6">
                                        <q-input v-model="p.quantity" :float-label="`${$t('quantity')}*`" type="number" :error="$v.products.$each[index].quantity.$error" @input="$v.products.$each[index].quantity.$touch" />
                                    </div>

                                    <div class="col-xs-12 col-sm-6">
                                        <q-input disable :float-label="$t('quantity_points')" type="number" :value="calculatePoints(p)" />
                                    </div>

                                    <div class="col-12" v-if="index > 0">
                                        <q-btn :label="$t('remove_product')" color="negative" @click="removeProduct(index)" />
                                    </div>
                                </div>
                            </q-card-main>
                        </q-card>
                    </div>

                    <div class="col-12">
                        <q-btn color="secondary" :label="$t('add_new_product')" @click="addProduct()" />
                    </div>

                    <div class="col-12 text-right">
                        <q-btn :label="$t('insert_sale')" color="positive" @click="submitUserData()" />
                    </div>
                </div>
            </div>
        </q-modal-layout>
    </q-modal>
</template>

<script>
import Uploader from 'components/Uploader'
import { mapGetters } from 'vuex'
import { uid, date } from 'quasar'
import { required, between, minValue } from 'vuelidate/lib/validators'
const today = new Date()
const { startOfDate, addToDate, subtractFromDate } = date
import * as moment from 'moment'
import * as tz from 'moment-timezone'

export default {
    i18nOptions: { namespaces: [ "custom", "common", "profile", "training", "admin", "rewards" ] },

    data() {
        return {
            branch: null,
            branches: [],
            brands: [],
            companies: [],
            company: null,
            filtered_branches: [],
            filtered_companies: [],
            loading: false,
            maximum_date: moment(new Date()).format('YYYY/MM/DD'),
            mininum_date: moment(new Date()).subtract(15, 'days').format('YYYY/MM/DD'),
            observation: null,
            order_number: null,
            order_number_error: false,
            processors: [],
            product_types: [],
            products: [{
                brand: null,
                product: null,
                product_code: null,
                product_type: null,
                quantity: null,
                quantity_points: 0
            }],
            sale_date: null,
            sale_images: [],
            sale_sent: false,
            show_insert_sale: false
        }
    },

    components: {
        'uploader': Uploader
    },

    computed: {
        ...mapGetters({
            access_token: 'common/access_token',
            areas: 'registration_lists/country_areas',
            reload_content: 'common/reload_content',
            reward_types: 'registration_lists/reward_types',
            reward_categories: 'registration_lists/reward_categories',
            id_user_language_selected: 'common/id_user_language_selected',
            num_rows: 'datatable/num_rows',
            user_id: 'campaigns/user_id'
        }),

        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        },

        getMaxWidth() {
            if (this.$q.platform.is.mobile) return '100vw'
            else return '100vw'
        },

        getMinWidth() {
            if (!this.$q.platform.is.mobile) return '100vw'
        },
    },

    methods: {
        getLists() {
            this.$q.loading.show()

            this.$axios.all([this.getSaleProducts(), this.getCompanies()])
            .then(this.$axios.spread((sale_products, companies) => {
                this.brands = sale_products.data.brands.map(e => ({ value: e.id, label: `${e.title} ${e.points > 0 ? `(${e.points} ${this.$t('point', { count: e.points})})` : ``}`, points: e.points }) )

                this.product_types = sale_products.data.sale_product_types.map(e => ({ value: e.id, label: `${e.title} ${e.points > 0 ? `(${e.points} ${this.$t('point', { count: e.points})})` : ``}`, points: e.points }) )

                this.processors = sale_products.data.sales_points.map(e => ({ value: e.id, label: `${e.title} ${e.points > 0 ? `(${e.points} ${this.$t('point', { count: e.points})})` : ``}`, points: e.points }) )

                this.companies = companies.data.map(e => ({ value: e.id, label: e.name }) )
            }))
            .catch(e => {
                console.log(e)
			})
			.finally(() => {
				this.$q.loading.hide()
			})
        },

        getSaleProducts() {
            return this.$axios.get(`/admin_rewards/reward_sale_products/${this.user_id}/`, this.getHeader)
        },

        getCompanies() {
            return this.$axios.get(`/admin_rewards/user_sale/${this.user_id}/companies/`, this.getHeader)
        },

        submitUserData() {
            this.$v.$touch()

            if (!this.$v.$error) {
                this.$q.loading.show()

                let data = {
                    order_number: this.order_number,
                    branch: this.branch,
                    observation: this.observation,
                    sale_date: moment(this.sale_date).tz('Etc/GMT').format('YYYY-MM-DD'),
                    sale_images: this.sale_images,
                    products: this.products.map(e => ({ brand: e.brand, product: e.product, product_code: e.product_code, product_type: e.product_type, quantity: parseInt(e.quantity) }) ),
                    user: this.user_id
                }

                this.$axios.post(`/admin_rewards/user_sale/`, data, this.getHeader)
                .then(response => {
                    this.$v.$reset()

                    this.$root.$emit('reloadItems', [])

                    this.$q.notify({
                        message: this.$t('user_sale_successfully_inserted'),
                        type: 'positive',
                        icon: 'fal fa-save'
                    })

                    this.show_insert_sale = false
                })
                .catch(e => {
                    console.log(e)

                    this.$q.notify({
                        message: this.$t('error_inserting_user_sale'),
                        type: 'negative',
                        icon: 'fal fa-ban'
                    })
                })
                .finally(() => {
                    this.$q.loading.hide()
                })
            }
        },

        uploadStarted() {
            this.loading = true
        },

        fileUploaded(url) {
            this.sale_images.push(url)
            this.loading = false
        },

        removeUserSaleImage(index) {
            this.sale_images.splice(index, 1)
        },

        calculatePoints(product) {
            let points = 0

            let brand_points = this.brands.find(e => e.value === product.brand) ? this.brands.find(e => e.value === product.brand).points : 0
            let product_type_points = this.product_types.find(e => e.value === product.product_type) ? this.product_types.find(e => e.value === product.product_type).points : 0
            let product_points = this.processors.find(e => e.value === product.product) ? this.processors.find(e => e.value === product.product).points : 0
            let product_quantity = product.quantity ? product.quantity : 0

            return (brand_points + product_type_points + product_points) * product_quantity
        },

        addProduct() {
            this.products.push({
                brand: null,
                product: null,
                product_code: null,
                product_type: null,
                quantity: null,
                quantity_points: 0
            })
        },

        removeProduct(index) {
            this.products.splice(index, 1)
        },

        changeCompany() {
            this.$v.company.$touch

            if (this.company) {
                this.loading = true

                this.$axios.get(`/admin_rewards/user_sale/${this.user_id}/company_branches/${this.company}/`, this.getHeader)
                .then(response => {
                    this.branches = response.data.map(e => ({ value: e.id, label: e.name }) )
                })
                .catch(e => {
                    console.log(e)
                })
                .finally(() => {
                    this.loading = false
                })
            }
        },

        clearSale() {
            this.branch = null
            this.company = null
            this.observation = null
            this.order_number = null
            this.products = [{
                brand: null,
                product: null,
                product_code: null,
                product_type: null,
                quantity: null,
                quantity_points: 0
            }]
            this.sale_date = null
            this.sale_images = []
            this.sale_sent = false
            this.order_number_error = false

            this.$v.$reset()
        },

        orderNumberUnique() {
            if (this.order_number) {
                let data = {
                    order_number: this.order_number,
                    user: this.user_id
                }

                this.$axios.post(`/admin_rewards/user_sale/check_user_order_number/`, data, this.getHeader)
                .then(response => {
                    if (response.data.exists) {
                        this.order_number_error = true
                    }
                    else this.order_number_error = false
                })
                .catch(e => {
                    console.log(e)
                })
            }
            else this.order_number_error = false
        }
    },

    validations() {
        return {
            branch: { required },
            company: { required },
            order_number: {
                required,
                orderNumberUnique() {
                    if (!this.order_number_error) return true
                    else return false
                }
            },
            products: {
                $each: {
                    brand: { required },
                    product: { required },
                    product_code: { required },
                    product_type: { required },
                    quantity: { required, minValue: minValue(1) }
                }
            },
            sale_date: {
                required,
                between (value, sale_date) {
                    return moment(value).isBetween(
                        moment(this.mininum_date).subtract(1, 'days'),
                        moment(this.maximum_date).add(1, 'days')
                    )
                }
            }
        }
    },

    created() {
        this.getLists()
    },

    mounted() {
		this.$root.$on('showInsertSale', () => {
            this.show_insert_sale = true
        })
	},

	beforeDestroy() {
		this.$root.$off('showInsertSale')
	}
}
</script>

<style lang="stylus" scoped>

</style>